import { Navigate, Outlet, useParams } from 'react-router-dom';
import { useUser } from '@/shared/hooks/useAuth';
import { hasWorkspaceAccess, type WorkspaceAccessKey } from '@/shared/permissions/workspaceAccess';
import { ROUTES } from './paths';
import type { CurrentUser } from '@/shared/types/auth';
import type { ReactNode } from 'react';

/** Where a denied/root navigation should land — a driver's only real page
 *  is the portal; everyone else falls back to the generic Dashboard. */
export function homeRouteFor(user: CurrentUser | null | undefined): string {
  return user?.role === 'driver' ? ROUTES.driverPortal : ROUTES.dashboard;
}

/** `access` may name several modules — holding ANY of them opens the route.
 *  Screens shared by two teams need this (Item Master, Print Barcodes and
 *  Rejection In/Out live under Dispatch but stay reachable by Accounts). */
export function WorkspaceRoute({ access, children }: { access: WorkspaceAccessKey | readonly WorkspaceAccessKey[]; children?: ReactNode }) {
  const user = useUser();
  const required = Array.isArray(access) ? access : [access as WorkspaceAccessKey];
  const allowed = required.some((key) => hasWorkspaceAccess(user, key));
  return allowed ? (children ?? <Outlet />) : <Navigate to={homeRouteFor(user)} replace />;
}

/** The `/` route target — sends a driver straight to their portal instead
 *  of the generic Dashboard everyone else lands on. */
export function RoleHomeRedirect() {
  const user = useUser();
  return <Navigate to={homeRouteFor(user)} replace />;
}

/** Guards a route that (unlike WorkspaceRoute-wrapped screens) has no
 *  workspace-access key of its own — Dashboard is open to any authenticated
 *  user by design, which would otherwise let a driver reach it directly by
 *  URL even though DRIVER_NAV_GROUPS never links to it. */
export function DriverPortalGate({ children }: { children: ReactNode }) {
  const user = useUser();
  return user?.role === 'driver' ? <Navigate to={ROUTES.driverPortal} replace /> : <>{children}</>;
}

/** Redirects the legacy `/accounts/parties/:id` path to its Accounting V2
 *  counterpart, carrying the id along — the two routes render the same
 *  PartyDetailWorkspacePage, so only one needs to stay canonical. */
export function PartyDetailRedirect() {
  const { id } = useParams();
  return <Navigate to={`${ROUTES.accountingV2Parties}/${id}`} replace />;
}
